/**
 * Media API Service
 * 
 * Bilder und andere Medien
 * 
 * Endpoints:
 * GET /media/{media_key} - Bild laden (Binary Response)
 */

import { API_BASE_URL, MEDIA_BASE_URL } from '../../config/constants';

/**
 * Konvertiert media_key zu vollständiger URL
 * Verwendet den separaten Media-Server falls konfiguriert
 * 
 * @param {string} mediaKey - z.B. "cover/manga/1.png" oder "avatar/user/42.png"
 * @returns {string|null} - Vollständige URL oder null
 */
export const getUrl = (mediaKey) => {
  if (!mediaKey) return null;
  
  // Wenn MEDIA_BASE_URL konfiguriert ist, diesen verwenden (CDN/Static)
  if (MEDIA_BASE_URL) {
    return `${MEDIA_BASE_URL}/${mediaKey}`;
  }
  
  // Fallback: API Endpoint
  return `${API_BASE_URL}/media/${encodeURIComponent(mediaKey)}`;
};

/**
 * Gibt die API-Endpoint URL für ein Bild zurück
 * Nutzt /api/v1/media/{media_key}
 * 
 * @param {string} mediaKey - z.B. "cover/manga/1.png"
 * @returns {string|null} - API URL oder null
 */
export const getApiUrl = (mediaKey) => {
  if (!mediaKey) return null;
  return `${API_BASE_URL}/media/${encodeURIComponent(mediaKey)}`;
};

/**
 * Generiert Cover-URL für einen Manga
 * @param {number} mangaId - Manga ID
 * @returns {string} - Cover URL
 */
export const getMangaCoverUrl = (mangaId) => {
  return getUrl(`cover/manga/${mangaId}.png`);
};

/**
 * Generiert Cover-URL für einen Band
 * @param {number} bandId - Band ID
 * @returns {string} - Cover URL
 */
export const getBandCoverUrl = (bandId) => {
  return getUrl(`cover/band/${bandId}.png`);
};

/**
 * Generiert Avatar-URL für einen User
 * @param {number} userId - User ID
 * @returns {string} - Avatar URL
 */
export const getUserAvatarUrl = (userId) => {
  return getUrl(`avatar/user/${userId}.png`);
};

/**
 * Placeholder-Bild URL
 * @param {string} type - 'manga', 'band', 'avatar'
 * @returns {string} - Placeholder URL
 */
export const getPlaceholderUrl = (type = 'manga') => {
  // Lokale Placeholder aus assets
  switch (type) {
    case 'avatar':
      return '/assets/placeholder-avatar.png';
    case 'band':
      return '/assets/placeholder-band.png';
    case 'manga':
    default:
      return '/assets/placeholder-manga.png';
  }
};

/**
 * Erstellt ein Image-Element mit Fallback
 * @param {string} mediaKey - Media Key
 * @param {string} fallbackType - Fallback-Typ für Placeholder
 * @returns {string} - URL mit Fallback-Logik (CSS verwendbar)
 */
export const getUrlWithFallback = (mediaKey, fallbackType = 'manga') => {
  return mediaKey ? getUrl(mediaKey) : getPlaceholderUrl(fallbackType);
};

// ============================================================================
// PRELOAD HELPERS
// ============================================================================

/**
 * Preloaded ein Bild im Hintergrund
 * @param {string} url - Bild URL
 * @returns {Promise<void>}
 */
export const preloadImage = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
    img.src = url;
  });
};

/**
 * Preloaded mehrere Bilder parallel
 * @param {string[]} urls - Array von URLs
 * @returns {Promise<void[]>}
 */
export const preloadImages = (urls) => {
  return Promise.all(urls.map(url => preloadImage(url).catch(() => null)));
};
